import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";
import { format } from "date-fns";
import PortalSidebar from "../PortalSidebar/PortalSidebar";

// helper to fetch with token
const fetchWithToken = async (endpoint, token) => {
  const response = await fetch(endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  return response.json();
};

// safer date formatter
const formatDate = (rawDate, fmt = "dd MMM yyyy HH:mm") => {
  const date = new Date(rawDate);
  if (isNaN(date.getTime())) return rawDate || "None";
  return format(date, fmt);
};

// pagination that matches the screenshot
const Pagination = ({
  currentPage,
  setCurrentPage,
  totalRecords,
  recordsPerPage,
}) => {
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = Math.min(startIndex + recordsPerPage, totalRecords);

  return (
    <div className={styles.docPaginationBar}>
      <button
        onClick={() => setCurrentPage(currentPage - 1)}
        disabled={currentPage === 1}
        className={styles.docPageBtn}
      >
        ‹
      </button>
      <span className={styles.docPageText}>
        {totalRecords === 0 ? "0" : startIndex + 1} – {endIndex} of{" "}
        {totalRecords}
      </span>
      <button
        onClick={() => setCurrentPage(currentPage + 1)}
        disabled={endIndex >= totalRecords}
        className={styles.docPageBtn}
      >
        ›
      </button>
    </div>
  );
};

const BuildingOwnerInfoPage = () => {
  const navigate = useNavigate();
  const { building_guid } = useParams();

  // ui
  const [isDropdownVisible, setDropdownVisible] = useState(false);

  // access + user
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [userName, setUserName] = useState("");

  // data
  const [buildingName, setBuildingName] = useState("");
  const [data, setData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  // doc filters
  const [docFilterName, setDocFilterName] = useState("");
  const [docFilterDesignation, setDocFilterDesignation] = useState("");
  const [docCurrentPage, setDocCurrentPage] = useState(1);
  const docRecordsPerPage = 5;

  // 1) validate building access
  const validateAccess = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(
        "https://portal.gmacc.co.za/api/access/check-building/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ building_guid }),
        }
      );
      const result = await res.json();
      if (res.ok && result.access) {
        setHasAccess(true);
      } else {
        alert("You do not have access to view this building.");
        navigate("/");
      }
    } catch (err) {
      console.error("Access validation failed", err);
      alert("Failed to check access.");
      navigate("/");
    } finally {
      setAccessChecked(true);
    }
  };

  useEffect(() => {
    validateAccess();
  }, [building_guid]);

  const [displayName, setDisplayName] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Default sidebar state based on device width
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

useEffect(() => {
  const handleResize = () => {
    const desktop = window.innerWidth >= 1024;
    setIsDesktop(desktop);

    // Only force closed when moving to smaller screens.
    // On desktop, keep whatever state the user last chose.
    if (!desktop) {
      setSidebarOpen(false);
    }
  };

  handleResize();
  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);
  useEffect(() => {
  function handleClick(e) {
    // On desktop/laptop, only the burger should control the sidebar
    if (isDesktop) return;

    const sidebar = document.getElementsByClassName(styles.sidebar)[0];
    const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

    if (!sidebar || !hamburger) return;

    const clickedInsideSidebar = sidebar.contains(e.target);
    const clickedHamburger = hamburger.contains(e.target);

    if (sidebarOpen && !clickedInsideSidebar && !clickedHamburger) {
      setSidebarOpen(false);
    }
  }

  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}, [sidebarOpen, isDesktop]);

  // pull user access for top-right dropdown
  useEffect(() => {
    const fetchUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const decoded = JSON.parse(atob(token.split(".")[1]));
        const { email, contact_id, firstname, lastname, name } = decoded || {};

        // Set display name safely
        const fullName =
          firstname && lastname
            ? `${firstname} ${lastname}`
            : name
            ? name
            : email || "User";

        setDisplayName(fullName);

        if (!email) {
          setLoadingAccess(false);
          return;
        }

        const resp = await fetch(
          "https://portal.gmacc.co.za/api/contact/determine-access/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email, contact_id }),
          }
        );

        const data = await resp.json();
        if (resp.ok) {
          setUserAccess(data.access || []);
        }
      } catch (err) {
        console.error("Failed to fetch access:", err);
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchUserAccess();
  }, [navigate]);

  // 3) fetch building info + docs (only one “tab” now)
  const fetchGeneralInfo = async (token) => {
    const endpoint = `https://portal.gmacc.co.za/api/building/${building_guid}/info/`;
    const result = await fetchWithToken(endpoint, token);
    setData(result);
    if (result?.cj_buildingname) {
      setBuildingName(result.cj_buildingname);
    }
  };

  const fetchGeneralDocs = async (token) => {
    const endpoint = `https://portal.gmacc.co.za/api/building/${building_guid}/docs_owner/`;
    try {
      const result = await fetchWithToken(endpoint, token);
      setDocuments(result.value || result);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  // run when access is confirmed
  useEffect(() => {
    const doFetch = async () => {
      if (!accessChecked || !hasAccess) return;
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No token found. Please log in.");
        navigate("/");
        return;
      }
      try {
        await fetchGeneralInfo(token);
        await fetchGeneralDocs(token);
      } catch (e) {
        console.error("Error fetching building data:", e);
      } finally {
        setLoading(false);
      }
    };

    doFetch();
  }, [accessChecked, hasAccess, building_guid, navigate]);

  // page title
  useEffect(() => {
    document.title = buildingName
      ? `GMA Portal || ${buildingName} Info`
      : "GMA Portal || Building Info";
  }, [buildingName]);
const handleLandlordPortalClick = () => {
    localStorage.setItem("rental_portal_mode", "landlord");
    navigate("/rental-portal");
  };

  const handleTenantPortalClick = () => {
    localStorage.setItem("rental_portal_mode", "tenant");
    navigate("/rental-portal");
  };
  // filtered / paginated docs
  const filteredDocs = documents.filter((doc) => {
    const nameMatch =
      !docFilterName ||
      doc.cj_name?.toLowerCase().includes(docFilterName.toLowerCase());
    const designation =
      doc["cj_documentdesignation@OData.Community.Display.V1.FormattedValue"] ||
      "";
    const designationMatch =
      !docFilterDesignation || designation === docFilterDesignation;
    return nameMatch && designationMatch;
  });

  const totalDocRecords = filteredDocs.length;
  const paginatedDocs = filteredDocs.slice(
    (docCurrentPage - 1) * docRecordsPerPage,
    (docCurrentPage - 1) * docRecordsPerPage + docRecordsPerPage
  );

  if (!accessChecked) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <main className={styles.contentArea}>
            <div className={styles.panel}>
              <div className={styles.loadingContainer}>
                <div className={styles.loadingImage}></div>
                <p className={styles.loadingText}>Checking Access...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!hasAccess) return null;

  return (
    <div className={styles.page}>
      {/* top bar like screenshot */}
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <img src={logoimage} alt="GMA Logo" className={styles.topLogo} />

          {/* HAMBURGER: opens/closes sidebar */}
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>

          <h1 className={styles.topTitle}>
            {buildingName ? `${buildingName} Information` : "Building Info"}
          </h1>
        </div>

        {/* USER CHIP: opens dropdown */}
        <div
          className={styles.userChip}
          onClick={() => {
            navigate("/user-profile");
          }}
          style={{ cursor: "pointer" }}
        >
          <span className={styles.statusDot}></span>
          <span className={styles.userName}>{displayName} ▾</span>
        </div>
      </header>

      {/* main layout */}
      <div className={styles.shell}>
<PortalSidebar
  activePortal="owner"
  activeTab="buildings"
  userAccess={userAccess}
  loadingAccess={loadingAccess}
  sidebarOpen={sidebarOpen}
  ticketRole="owner"
/>

        {/* right content */}
        <main className={styles.contentArea}>
          <div className={styles.panel}>
            {loading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingImage}></div>
                <p className={styles.loadingText}>Loading...</p>
              </div>
            ) : (
              <div className={styles.generalTwoColumnLayout}>
                {/* ================= LEFT SIDE — GENERAL INFO ================= */}
                <div className={styles.generalLeftColumn}>
                  <div className={styles.cardTopBarCentered}>GENERAL</div>

                  {!data ? (
                    <div className={styles.errorText}>
                      <p>No building info found.</p>
                    </div>
                  ) : (
                    <>
                      <div className={styles.generalFieldRow}>
                        <div className={styles.generalFieldLabel}>
                          Building Name:
                        </div>
                        <div className={styles.generalFieldValue}>
                          {data.cj_buildingname || "—"}
                        </div>
                      </div>

                      <div className={styles.generalFieldRow}>
                        <div className={styles.generalFieldLabel}>
                          Caretaker:
                        </div>
                        <div className={styles.generalFieldValue}>
                          {
                            data[
                              "_cj_caretaker_value@OData.Community.Display.V1.FormattedValue"
                            ]
                          }
                        </div>
                      </div>

                      <div className={styles.generalFieldRow}>
                        <div className={styles.generalFieldLabel}>
                          Number of Trustees:
                        </div>
                        <div className={styles.generalFieldValue}>
                          {data[
                            "ab_numberoftrustees@OData.Community.Display.V1.FormattedValue"
                          ] ||
                            data.ab_numberoftrustees ||
                            "—"}
                        </div>
                      </div>

                      <div className={styles.generalFieldRow}>
                        <div className={styles.generalFieldLabel}>
                          Insurance Broker:
                        </div>

                        <div className={styles.generalFieldValue}>
                          {
                            data[
                              "_cj_insurancebrokercontractor_value@OData.Community.Display.V1.FormattedValue"
                            ]
                          }{" "}
                          || Mobile:{" "}
                          {
                            data.cj_InsuranceBrokerContractor
                              ?.cj_companytelephone
                          }{" "}
                          || Email:{" "}
                          {data.cj_InsuranceBrokerContractor?.emailaddress}
                        </div>
                      </div>

                      <div className={styles.generalFieldRow}>
                        <div className={styles.generalFieldLabel}>Insurer:</div>

                        <div className={styles.generalFieldValue}>
                          {
                            data[
                              "_cj_insurercontractor_value@OData.Community.Display.V1.FormattedValue"
                            ]
                          }
                        </div>
                      </div>

                      <div className={styles.generalFieldRow}>
                        <div className={styles.generalFieldLabel}>
                          Insurance Evaluation Date:
                        </div>
                        <div className={styles.generalFieldValue}>
                          {formatDate(data.ab_insurancenext3years)}
                        </div>
                      </div>

                      <div className={styles.generalFieldRow}>
                        <div className={styles.generalFieldLabel}>
                          Registration Number:
                        </div>
                        <div className={styles.generalFieldValue}>
                          {data.cj_registrationnumber || "None"}
                        </div>
                      </div>

                      <div className={styles.generalFieldRow}>
                        <div className={styles.generalFieldLabel}>
                          CSOS Reg Number:
                        </div>
                        <div className={styles.generalFieldValue}>
                          {data.ab_csosregnumber || "None"}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* ================= RIGHT SIDE — DOCUMENTS PANEL ================= */}
                <div className={styles.generalDocsPanel}>
                  <div className={styles.cardTopBarCentered}>DOCUMENTS</div>

                  <div className={styles.generalDocsFilterRow}>
                    <input
                      type="text"
                      placeholder="Filter Document Name"
                      value={docFilterName}
                      onChange={(e) => {
                        setDocFilterName(e.target.value);
                        setDocCurrentPage(1);
                      }}
                      className={styles.generalDocsSearchInput}
                    />

                    <select
                      value={docFilterDesignation}
                      onChange={(e) => {
                        setDocFilterDesignation(e.target.value);
                        setDocCurrentPage(1);
                      }}
                      className={styles.generalDocsSelect}
                    >
                      <option value="">All Designations</option>
                      {[
                        "Audit",
                        "Bank Confirmation",
                        "Budget",
                        "Complex Policy",
                        "Conduct Rules",
                        "Credit Policy",
                        "Insurance Policy",
                        "Minutes",
                        "Newsletter",
                        "Other",
                        "SS Plan",
                      ].map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>

                    <button
                      className={styles.generalDocsClearBtn}
                      onClick={() => {
                        setDocFilterName("");
                        setDocFilterDesignation("");
                        setDocCurrentPage(1);
                      }}
                    >
                      Clear Filters
                    </button>
                  </div>

                  {filteredDocs.length > 0 ? (
                    <>
                      <div className={styles.generalDocsTableWrapper}>
                        <table className={styles.generalDocsTable}>
                          <thead>
                            <tr>
                              <th>Document Name</th>
                              <th>Designation</th>
                              <th>Download</th>
                            </tr>
                          </thead>

                          <tbody>
                            {paginatedDocs.map((doc) => (
                              <tr key={doc.cj_documentid}>
                                <td>{doc.cj_name}</td>
                                <td>
                                  {
                                    doc[
                                      "cj_documentdesignation@OData.Community.Display.V1.FormattedValue"
                                    ]
                                  }
                                </td>
                                <td>
                                  <a
                                    href={`https://portal.gmacc.co.za/api/building/document/${doc.cj_documentid}/download/`}
                                    download={doc.cj_content_name}
                                    className={styles.viewFileBtn}
                                  >
                                    Download
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className={styles.pagination}>
                        <button
                          className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
                          disabled={docCurrentPage === 1}
                          onClick={() => setDocCurrentPage(docCurrentPage - 1)}
                        >
                          Previous
                        </button>

                        <span className={styles.pageMeta}>
                          {totalDocRecords === 0
                            ? "0–0 of 0"
                            : `${
                                (docCurrentPage - 1) * docRecordsPerPage + 1
                              }–${Math.min(
                                docCurrentPage * docRecordsPerPage,
                                totalDocRecords
                              )} of ${totalDocRecords}`}
                        </span>

                        <button
                          className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
                          disabled={
                            docCurrentPage * docRecordsPerPage >=
                            totalDocRecords
                          }
                          onClick={() => setDocCurrentPage(docCurrentPage + 1)}
                        >
                          Next
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className={styles.errorText}>
                      <p>No documents found.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default BuildingOwnerInfoPage;
